import { Autocomplete, TextField } from "@mui/material";
import { isString } from "lodash";
import {
  CustomFieldInputComponent,
  CustomFieldInputProps,
} from "../CustomFieldsForm";
import {
  CustomFieldSelectOption,
  CustomFieldSelectOptionEntity,
  useGetDriverListQuery,
  useGetTractorListQuery,
  useGetTrailerListQuery,
} from "../../../../graphql/generated";
import driverLabel from "../../../../utils/labels/driverLabel";
import trailerLabel from "../../../../utils/labels/trailerLabel";
import tractorLabel from "../../../../utils/labels/tractorLabel";
import { useEffect, useState } from "react";
import { testIds } from "./SelectCustomFieldInput.testIds";
import activeOrSelected from "../../../../utils/assets/activeOrSelected";

export const getCustomFieldOptions = async (
  customFieldDefinition: CustomFieldInputProps["customFieldDefinition"],
  selectedValues: readonly (string | undefined)[] = []
): Promise<CustomFieldSelectOption[]> => {
  if (customFieldDefinition.selectOptionsEntity) {
    if (
      customFieldDefinition.selectOptionsEntity ===
      CustomFieldSelectOptionEntity.Driver
    ) {
      const {
        drivers: { data },
      } = await useGetDriverListQuery.fetcher({ includeInactive: true })();
      return activeOrSelected(data, selectedValues).map((driver) => ({
        key: driver._id,
        label: driverLabel(driver),
      }));
    }

    if (
      customFieldDefinition.selectOptionsEntity ===
      CustomFieldSelectOptionEntity.Tractor
    ) {
      const {
        tractors: { data },
      } = await useGetTractorListQuery.fetcher({ includeInactive: true })();
      return activeOrSelected(data, selectedValues).map((tractor) => ({
        key: tractor._id,
        label: tractorLabel(tractor),
      }));
    }

    if (
      customFieldDefinition.selectOptionsEntity ===
      CustomFieldSelectOptionEntity.Trailer
    ) {
      const {
        trailers: { data },
      } = await useGetTrailerListQuery.fetcher({ includeInactive: true })();
      return activeOrSelected(data, selectedValues).map((trailer) => ({
        key: trailer._id,
        label: trailerLabel(trailer),
      }));
    }
  }

  return customFieldDefinition.selectOptions || [];
};

const SelectCustomFieldInput: CustomFieldInputComponent = ({
  value,
  customFieldDefinition,
  onChange,
  disabled,
}) => {
  const [options, setOptions] = useState<CustomFieldSelectOption[]>([]);

  useEffect(() => {
    getCustomFieldOptions(
      customFieldDefinition,
      isString(value) ? [value] : []
    ).then(setOptions);
  }, [customFieldDefinition, value]);

  return (
    <Autocomplete data-testid={testIds.selectCustomFieldInputPicker}
      options={options.map((option) => option.key) || []}
      getOptionLabel={(optionKey) =>
        options.find((selectOption) => selectOption.key === optionKey)?.label ||
        ""
      }
      onChange={(event, value) => {
        onChange(value);
      }}
      value={isString(value) ? value : null}
      size="medium"
      fullWidth
      disabled={disabled}
      renderInput={(inputProps) => (
        <TextField
          label={customFieldDefinition.label}
          {...inputProps}
          inputProps={{ ...inputProps.inputProps, "data-testid": testIds.selectCustomFieldInputSearch }}
        />
      )}
    />
  );
};

export default SelectCustomFieldInput;
